import { useEffect, useState } from "react";
import { Play, FileText } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/States";
import { StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { fetchContent, resolveMedia, youtubeEmbed } from "@/lib/contentApi";

export default function OurResults() {
  useSeo({
    title: "Our Results",
    description:
      "Real ASAI students, real placements, real stories. Explore verified student placement results and testimonials from the Atmasamman School of AI.",
    path: "/our-results",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Our Results", path: "/our-results" }],
  });

  const [placements, setPlacements] = useState(null);
  const [testimonials, setTestimonials] = useState(null);

  useEffect(() => {
    fetchContent("placements").then(setPlacements).catch(() => setPlacements([]));
    fetchContent("resultTestimonials").then(setTestimonials).catch(() => setTestimonials([]));
  }, []);

  const tMap = {};
  (testimonials || []).forEach((t) => { if (t.studentSlug) tMap[t.studentSlug] = t; });

  const scrollToStory = (slug) => {
    const el = document.getElementById(`testimonial-${slug}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-orange");
      setTimeout(() => el.classList.remove("ring-2", "ring-orange"), 2000);
    }
  };

  return (
    <div data-testid="page-our-results">
      <PageHeader
        overline="Our Results"
        titleLines={["Real Students. Real Results.", "Real Stories."]}
        description="Celebrating the achievements, career journeys, and experiences of ASAI students."
      />

      {/* Section 1 — Placement Results */}
      <section className="bg-white py-16 md:py-24" data-testid="placements-section">
        <div className="container-asai">
          <SectionHeading overline="Placement Results" title="Where our students are now" />
          {placements === null ? null : placements.length === 0 ? (
            <div className="mt-10">
              <EmptyState
                title="Placement results coming soon"
                description="We're adding verified student placement results. Only genuine, authorized placements are published here — check back soon."
              />
            </div>
          ) : (
            <StaggerGroup className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="placements-grid">
              {placements.map((p) => {
                const linked = p.studentSlug && tMap[p.studentSlug];
                return (
                  <StaggerItem
                    key={p.id}
                    data-testid="placement-card"
                    className="group flex flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-lightgrey">
                      {p.photo ? (
                        <img
                          src={resolveMedia(p.photo)}
                          alt={p.studentName}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center font-heading text-6xl text-navy/15">
                          {(p.studentName || "S")[0]}
                        </div>
                      )}
                      {p.company && (
                        <span className="absolute left-3 top-3 rounded-full bg-navy/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                          {p.company}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col gap-1 p-5">
                      <h3 className="font-heading text-xl leading-tight text-navy">{p.studentName}</h3>
                      {p.designation && <p className="text-sm font-semibold text-orange">{p.designation}</p>}
                      <div className="mt-1 space-y-0.5 text-sm text-navy/60">
                        {p.course && <p>{p.course}</p>}
                        {p.placementDate && <p>Placed: {p.placementDate}</p>}
                      </div>
                      {(p.document || linked) && (
                        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-navy/5 pt-4">
                          {p.document && (
                            <a
                              href={resolveMedia(p.document)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="placement-document"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-navy/70 transition-colors hover:text-orange"
                            >
                              <FileText className="h-3.5 w-3.5" /> Placement proof
                            </a>
                          )}
                          {linked && (
                            <button
                              onClick={() => scrollToStory(p.studentSlug)}
                              data-testid="watch-experience"
                              className="inline-flex items-center gap-1.5 rounded-full bg-orange px-3.5 py-1.5 text-xs font-semibold text-white transition-transform duration-300 hover:scale-105"
                            >
                              <Play className="h-3.5 w-3.5 fill-current" /> Watch Student Experience
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          )}
        </div>
      </section>

      {/* Section 2 — Testimonials */}
      <section className="bg-lightgrey py-16 md:py-24" data-testid="testimonials-section">
        <div className="container-asai">
          <SectionHeading overline="Student Voices" title="Hear From Our Students" />
          {testimonials === null ? null : testimonials.length === 0 ? (
            <div className="mt-10">
              <EmptyState
                title="Student stories coming soon"
                description="We're collecting authentic testimonials from our students. Verified stories and videos will appear here soon — we never publish fake reviews."
              />
            </div>
          ) : (
            <StaggerGroup className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="results-testimonials-grid">
              {testimonials.map((t) => {
                const embed = youtubeEmbed(t.youtubeUrl);
                return (
                  <StaggerItem
                    key={t.id}
                    id={t.studentSlug ? `testimonial-${t.studentSlug}` : undefined}
                    data-testid="result-testimonial-card"
                    className="flex flex-col gap-4 rounded-2xl border border-navy/10 bg-white p-5 shadow-sm transition-all duration-300"
                  >
                    {embed ? (
                      <div className="aspect-video overflow-hidden rounded-xl bg-black">
                        <iframe
                          src={embed}
                          title={t.studentName}
                          loading="lazy"
                          className="h-full w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : t.videoFile ? (
                      <video src={resolveMedia(t.videoFile)} controls className="aspect-video w-full rounded-xl bg-black object-cover" />
                    ) : null}
                    {t.feedback && (
                      <p className="font-heading text-base leading-snug text-navy/85">&ldquo;{t.feedback}&rdquo;</p>
                    )}
                    <div className="mt-auto flex items-center gap-3">
                      {t.photo ? (
                        <img src={resolveMedia(t.photo)} alt={t.studentName} loading="lazy" className="h-11 w-11 rounded-full object-cover" />
                      ) : (
                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-navy font-semibold text-white">
                          {(t.studentName || "S")[0]}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-semibold text-navy">{t.studentName}</div>
                        {t.course && <div className="text-xs text-navy/55">{t.course}</div>}
                      </div>
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          )}
        </div>
      </section>
    </div>
  );
}
